import { Box, PopperPlacementType, SxProps, Theme } from '@mui/material';
import { ReactNode, useCallback } from 'react';

import { HOVER_HALO_COLOR, HOVER_HALO_OUTLINE } from '@/styles/tokens/colors';

import {
  getPresentationHoverOutlineSx,
  usePresentationRowHover,
} from './PresentationHoverActions.hooks';
import { PresentationRowHoverToolbar } from './PresentationRowHoverToolbar';

interface PresentationBlockHoverRegionProps {
  /**
   * Stable id for this hover region within the presentation. Two regions on
   * the same slide (e.g. a cover slide's title block and its details block)
   * must use distinct ids so only one shows its toolbar at a time.
   */
  id: string;
  /**
   * The floating toolbar's contents. Receives `dismiss` so an action can hide
   * the toolbar once it runs (mirrors the row toolbar's `onDismiss`).
   */
  renderToolbar: (args: { dismiss: () => void }) => ReactNode;
  /**
   * Suppress the hover treatment (e.g. an edit overlay launched from here is
   * open and the outline behind it would just be noise).
   */
  disabled?: boolean;
  /** Toolbar placement against the block. Defaults to the top-left corner. */
  placement?: PopperPlacementType;
  /**
   * Add 8px of breathing room between the content and the halo so it doesn't
   * hug the content. Rendered as an absolutely-positioned overlay inset by
   * -8px (rather than an outline on the content box), so the halo keeps the
   * common hover radius and never resizes the box or reflows neighbouring
   * content when it appears.
   */
  haloPadding?: boolean;
  /** Applied to the wrapping element alongside the hover outline. */
  sx?: SxProps<Theme>;
  children: ReactNode;
}

/**
 * Wraps a block of slide content (a whole section, not a table row) with the
 * presentation blue-hover treatment: a halo outline on hover and a floating
 * action toolbar anchored to the block. Outside a
 * `PresentationHoverActionsProvider` the hook reports inactive, so the block
 * renders plainly — the same component is inert in print/preview contexts.
 */
export function PresentationBlockHoverRegion({
  id,
  renderToolbar,
  disabled = false,
  placement = 'top-start',
  haloPadding = false,
  sx,
  children,
}: PresentationBlockHoverRegionProps) {
  const {
    isOutlineActive,
    isToolbarActive,
    anchorRef,
    rowProps,
    toolbarProps,
    dismiss,
  } = usePresentationRowHover(id, { disabled });

  const setAnchor = useCallback(
    (node: HTMLDivElement | null) => {
      anchorRef.current = node;
    },
    [anchorRef]
  );

  return (
    <Box
      ref={setAnchor}
      sx={[
        ...(Array.isArray(sx) ? sx : [sx]),
        // The padded halo is drawn as the out-of-flow overlay below (anchored
        // to this box); the plain variant applies the shared hover outline
        // directly to the content box.
        haloPadding
          ? { position: 'relative' }
          : getPresentationHoverOutlineSx(isOutlineActive),
      ]}
      {...rowProps}
    >
      {children}
      {haloPadding && isOutlineActive && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            // 8px of breathing room on every side. Out of flow, so it neither
            // resizes the content box nor reflows neighbouring content.
            inset: '-8px',
            border: `1px solid ${HOVER_HALO_COLOR}`,
            // Reuse the common hover radius rather than letting an outline
            // offset inflate it.
            borderRadius: HOVER_HALO_OUTLINE.borderRadius,
            pointerEvents: 'none',
          }}
        />
      )}
      {isToolbarActive && anchorRef.current && (
        <PresentationRowHoverToolbar
          anchorEl={anchorRef.current}
          placement={placement}
          onMouseEnter={toolbarProps.onMouseEnter}
          onMouseLeave={toolbarProps.onMouseLeave}
        >
          {renderToolbar({ dismiss })}
        </PresentationRowHoverToolbar>
      )}
    </Box>
  );
}
